import { GIButton, Modal } from 'app-components';
import React, { Dispatch, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ModalContextType } from '../../../../contexts/modalContext';
import useApiHandling from '../../hooks/useApiHandling';
import CommonTextArea from '../../common/input/CommonTextArea';
import {
    ConditionalRendering,
    CsrOptionName,
    environmentSelector,
    fetchApplicationsDetails,
    INPUT_MAX_LENGTH,
    RequestStatus,
    useAppDispatch,
    useAppSelector,
    ValidationErrorMessage,
} from '../../utils';

const StyledModalBoday = styled.div`
    @media (max-width: 768px) {
        .gi-modal__body {
            max-height: 0%;
            padding-bottom: 0px;
        }
    }
`;

const StyledCharacter = styled.div`
    text-align: left;
`;

const StyledModalControls = styled.div`
    display: flex;
    justify-content: flex-end;

    .gap-2 {
        display: flex;
        gap: 10px;
    }

    @media (max-width: 768px) {
        flex-direction: column-reverse;
    }
`;

const InputContainer = styled.div`
    margin-top: 20px;
    margin-bottom: 5px;
`;

const Label = styled.label`
    color: ${({ theme }) => theme.infoMain};
    margin-bottom: 5px;
    display: block;
    font-size: 16px;
    font-weight: 600;

    span {
        color: ${({ theme }) => theme.dangerMain};
        margin-left: 5px;
    }

    @media (max-width: 480px) {
    }
`;

const StyledBackgroundColor = styled.p`
    background-color: ${({ theme }) => theme.infoLightest};
    color: ${({ theme }) => theme.infoMain};
    padding: 30px 20px;

    @media (max-width: 480px) {
        padding: 8px;
    }
`;

interface IModalProps extends ModalContextType {
    caseNumber: string;
    typeOfCsrAction: CsrOptionName;
    coverageYear: number;
    applicationType: string;
}

const CsrOverrideCommentModal: React.FC = ({
    openModal,
    closeModal,
    caseNumber,
    typeOfCsrAction,
    coverageYear,
    applicationType,
}: IModalProps) => {
    const [overrideComment, setOverrideComment] = useState('');
    const {
        checkActive,
        addOverrideComment,
        reruneligibility,
        getSepDetails,
        initVerifications,
        sendobat,
        updateCarrier,
        submitOverrideApplicationEditLock,
        submitEndSepModal,
        handleEditApplication,
    } = useApiHandling();

    const [errorMsg, setErrorMsg] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const dispatch: Dispatch<any> = useAppDispatch();
    const currentYear = useAppSelector(state => state.capApplications.currentYear);
    const environment = useAppSelector(environmentSelector);

    function onClose() {
        closeModal('csrOverrideCommentModal');
    }

    async function onSubmit() {
        if (typeOfCsrAction === CsrOptionName.END_SEP) {
            const response = await submitEndSepModal(caseNumber, overrideComment);
            if (response === RequestStatus.FAILURE) {
                setErrorMsg('cap.application.modal.endSep.error');
            }
            dispatch(
                fetchApplicationsDetails(currentYear, environment, () => {
                    setIsLoading(false);
                }),
            );
            return;
        }

        const response = await addOverrideComment(caseNumber, overrideComment);
        if (response?.message?.toLowerCase() === RequestStatus.SUCCESS || response === RequestStatus.SUCCESS) {
            switch (typeOfCsrAction) {
                case CsrOptionName.OVERRIDE_APP_EDIT_LOCK:
                    return submitOverrideApplicationEditLock(caseNumber, overrideComment, applicationType);
                case CsrOptionName.OVERRIDE_SEP_DENIAL:
                    return checkActive(coverageYear, caseNumber);
                case CsrOptionName.RE_RUN:
                    return reruneligibility(caseNumber);
                case CsrOptionName.INITIATE:
                    return initVerifications(caseNumber);
                case CsrOptionName.SEND_OUTBOUND_AT:
                    return sendobat(caseNumber);
                case CsrOptionName.UPDATE:
                    return updateCarrier(caseNumber);
                case CsrOptionName.EDIT:
                    return handleEditApplication(caseNumber);
                case CsrOptionName.CANCEL_TERM:
                    onClose();
                    return openModal('cancelTerminatePlanModal', { caseNumber });
                case CsrOptionName.SPECIAL_ENROLLMENT:
                    return getSepDetails(caseNumber, CsrOptionName.SPECIAL_ENROLLMENT, coverageYear);
                case CsrOptionName.COVERAGE_DATE:
                    return getSepDetails(caseNumber, CsrOptionName.COVERAGE_DATE, coverageYear);
                default:
                    return;
            }
        } else {
            return;
        }
    }

    const getModalHeader = (): string => {
        switch (typeOfCsrAction) {
            case CsrOptionName.SPECIAL_ENROLLMENT:
                return 'cap.applications.modal.openspecialenorollment2';
            case CsrOptionName.COVERAGE_DATE:
                return 'cap.applications.modal.changecoveragedate2';
            case CsrOptionName.EDIT:
                return 'cap.applications.modal.editapplication2';
            case CsrOptionName.INITIATE:
                return 'cap.applications.modal.initiateverifications2';
            case CsrOptionName.RE_RUN:
                return 'cap.applications.modal.reruneligibility2';
            case CsrOptionName.CANCEL_TERM:
                return 'cap.applications.modal.cancelorterminatemyplan2';
            case CsrOptionName.UPDATE:
                return 'cap.applications.modal.updatecarrier2';
            case CsrOptionName.SEND_OUTBOUND_AT:
                return 'cap.applications.modal.sendObAt.title';
            case CsrOptionName.OVERRIDE_SEP_DENIAL:
                return 'cap.applications.modal.overridesepdenial';
            case CsrOptionName.OVERRIDE_APP_EDIT_LOCK:
                return 'cap.applications.modal.overrideApplicationEditLock.header';
            case CsrOptionName.END_SEP:
                return 'cap.application.modal.endSap.header';
            default:
                return '';
        }
    };

    return (
        <Modal id="change_coverage_start_date_modal">
            <Modal.Header id="change_coverage_start_date_modal_title" titleId={getModalHeader()} onHide={onClose} />
            <StyledModalBoday>
                <Modal.Body id="change_coverage_start_date_modal_body">
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.END_SEP}>
                        <ValidationErrorMessage isVisible={!!errorMsg} messageText={errorMsg} />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.SPECIAL_ENROLLMENT}>
                        <FormattedMessage id="cap.application.modal.openspecialenorollmentcontent" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.COVERAGE_DATE}>
                        <FormattedMessage id="cap.application.modal.changecoveragedatecontent" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.EDIT}>
                        <FormattedMessage 
                            id="cap.application.modal.editapplicationcontent"
                            values={{
                                br: <br/>,
                                strong: chunks => <strong>{chunks}</strong>
                            }}
                        />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.INITIATE}>
                        <FormattedMessage id="cap.application.modal.initiateverificationscontent" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.RE_RUN}>
                        <FormattedMessage id="cap.application.modal.elirerungibilitycontent" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.CANCEL_TERM}>
                        <FormattedMessage id="cap.application.modal.cancelorterminatemyplancontent" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.UPDATE}>
                        <FormattedMessage id="cap.application.modal.updatecarriercontent" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.SEND_OUTBOUND_AT}>
                        <FormattedMessage id="cap.application.modal.sendObAt.content" />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.OVERRIDE_SEP_DENIAL}>
                        <FormattedMessage id="cap.application.modal.overridesepdenialcontent" />
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.END_SEP}>
                        <StyledBackgroundColor data-testid="cap_application_modal_end_sep_content">
                            <strong>
                                <FormattedMessage id="cap.application.modal.overrideApplicationEditLock.p1.header" />
                            </strong>
                            <br />
                            <FormattedMessage id="cap.application.modal.endsep.p2" />
                        </StyledBackgroundColor>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.OVERRIDE_APP_EDIT_LOCK}>
                        <StyledBackgroundColor data-testid="cap_application_modal_override_application_content">
                            <strong>
                                <FormattedMessage id="cap.application.modal.overrideApplicationEditLock.p1.header" />
                            </strong>
                            <br />
                            <FormattedMessage id="cap.application.modal.overrideApplicationEditLock.p1" />
                        </StyledBackgroundColor>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={typeOfCsrAction === CsrOptionName.OVERRIDE_APP_EDIT_LOCK}>
                        <p data-testid="cap_application_modal_override_application_content_2">
                            <FormattedMessage id="cap.application.modal.overrideApplicationEditLock.p2" />
                        </p>
                    </ConditionalRendering>

                    <InputContainer>
                        <Label data-testid="change_coverage_start_date_modal_input_label" htmlFor='change_coverage_start_date_modal_textarea'>
                            <FormattedMessage id="cap.applications.modal.changeCoverageLabel" />: <span>*</span>
                        </Label>
                        <CommonTextArea
                            id="change_coverage_start_date_modal_textarea"
                            value={overrideComment}
                            setValue={(value: string) => value.length <= INPUT_MAX_LENGTH && setOverrideComment(value)}
                            placeholder={'cap.applications.modal.explainPlaceholder'}
                        />
                    </InputContainer>
                    <StyledCharacter data-testid="cap_application_modal_override_application_chars_left">
                        {INPUT_MAX_LENGTH - overrideComment.trim().length}{' '}
                        <FormattedMessage id="cap.applications.modal.charactersLeft" />
                    </StyledCharacter>
                </Modal.Body>
            </StyledModalBoday>
            <Modal.Controls>
                <StyledModalControls>
                    <div className="gap-2">
                        <GIButton
                            id="change_coverage_start_date_modal_continue_button"
                            labelId="cap.applications.modal.button.label.continue"
                            isDisabled={!overrideComment.trim()}
                            onClickHandler={onSubmit}
                        ></GIButton>
                        <GIButton
                            id="change_coverage_start_date_modal_cancel_button"
                            labelId="cap.applications.modal.button.label.cancel"
                            variant="secondary"
                            onClickHandler={onClose}
                        ></GIButton>
                    </div>
                </StyledModalControls>
            </Modal.Controls>
        </Modal>
    );
};

export default CsrOverrideCommentModal;
